import type {
  IncidentNarrative,
  IncidentRelationships,
  OperationalImpact,
  Recommendation,
  RootCauseAnalysis,
  TimelineEvent,
} from "@/types";
import { formatRelativeTime } from "@/lib/format";
import { useState, type ReactNode } from "react";

interface IncidentIntelligencePanelProps {
  timeline: TimelineEvent[];
  rootCause: RootCauseAnalysis | null;
  operationalImpact: OperationalImpact | null;
  recommendations: Recommendation[];
  narrative: IncidentNarrative | null;
  relationships: IncidentRelationships | null;
  correlatedIncidentIds: string[];
  /** When set, recommendations with an actionId become executable. */
  onExecuteAction?: (actionId: string) => void;
  actionsRunning?: boolean;
}

const EVENT_TYPE_LABELS: Record<TimelineEvent["eventType"], string> = {
  deployment: "Deployment",
  verification: "Verification",
  policy_evaluation: "Policy",
  recovery: "Recovery",
  incident_opened: "Opened",
  incident_resolved: "Resolved",
  other: "Event",
};

const METHOD_LABELS: Record<RootCauseAnalysis["method"], string> = {
  deployment_correlation: "Deployment correlation",
  policy_evaluation: "Policy evaluation",
  single_component_failure: "Single component failure",
  insufficient_evidence: "Insufficient evidence",
};

function confidencePercent(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

function formatInputValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function shortAction(action: string): string {
  const text = action.trim();
  if (/verify tenant API is running and reachable/i.test(text)) return "Restart tenant API";
  if (text.length > 72) return `${text.slice(0, 69).trimEnd()}…`;
  return text;
}

function IntelGroup({
  title,
  summary,
  defaultOpen = false,
  children,
}: {
  title: string;
  summary?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="iw-intelligence__block iw-intelligence__group">
      <button
        type="button"
        className="btn btn-ghost iw-evidence-toggle iw-intelligence__group-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">{open ? "▼" : "▶"}</span> {title}
        {!open && summary ? <span className="iw-intelligence__group-summary">{summary}</span> : null}
      </button>
      {open ? <div className="iw-intelligence__group-body">{children}</div> : null}
    </div>
  );
}

/** Incident Intelligence Engine — collapsible groups; Root Cause open by default. */
export function IncidentIntelligencePanel({
  timeline,
  rootCause,
  operationalImpact,
  recommendations,
  narrative,
  relationships,
  correlatedIncidentIds,
  onExecuteAction,
  actionsRunning = false,
}: IncidentIntelligencePanelProps) {
  const [showInputs, setShowInputs] = useState(false);

  const hasRelationships =
    relationships &&
    (relationships.parentIncidentId ||
      relationships.childIncidentIds.length > 0 ||
      relationships.duplicateIncidentIds.length > 0 ||
      relationships.recurring ||
      relationships.relatedDeploymentIds.length > 0);

  const hasAnything =
    rootCause ||
    operationalImpact ||
    recommendations.length > 0 ||
    hasRelationships ||
    timeline.length > 0 ||
    narrative ||
    correlatedIncidentIds.length > 0;

  if (!hasAnything) {
    return null;
  }

  return (
    <section className="iw-section iw-intelligence">
      <h3 className="iw-section__title iw-section__title--prominent">Incident Intelligence</h3>

      {rootCause ? (
        <IntelGroup
          title="Root Cause"
          defaultOpen
          summary={`${rootCause.confidenceLabel} · ${shortAction(rootCause.summary)}`}
        >
          <p className="iw-intelligence__summary">{rootCause.summary}</p>
          <div className="iw-intelligence__confidence-row">
            <span className={`iw-intelligence__badge iw-intelligence__badge--${rootCause.confidenceLabel}`}>
              {rootCause.confidenceLabel} confidence ({confidencePercent(rootCause.confidence)})
            </span>
            <span className="iw-intelligence__method">Method: {METHOD_LABELS[rootCause.method]}</span>
          </div>
          {rootCause.supportingEvidence.length > 0 ? (
            <ul className="drawer-list">
              {rootCause.supportingEvidence.map((line) => (
                <li key={line} className="drawer-list__item">
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {rootCause.excludedCauses.length > 0 ? (
            <div className="iw-intelligence__excluded">
              <span className="iw-confidence__reasons-title">Excluded causes</span>
              <ul className="drawer-list">
                {rootCause.excludedCauses.map((excluded) => (
                  <li key={excluded.cause} className="drawer-list__item">
                    <strong>{excluded.cause}</strong>
                    <span>{excluded.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {rootCause.inputs && Object.keys(rootCause.inputs).length > 0 ? (
            <div className="iw-intelligence__inputs">
              <button
                type="button"
                className="btn btn-ghost iw-evidence-toggle"
                onClick={() => setShowInputs((v) => !v)}
              >
                {showInputs ? "Hide analysis inputs" : "▶ Show analysis inputs"}
              </button>
              {showInputs ? (
                <ul className="drawer-list">
                  {Object.entries(rootCause.inputs).map(([key, value]) => (
                    <li key={key} className="drawer-list__item">
                      <strong>{key.replace(/_/g, " ")}</strong>
                      <span>{formatInputValue(value)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </IntelGroup>
      ) : null}

      {operationalImpact ? (
        <IntelGroup
          title="Operational Impact"
          summary={`${operationalImpact.estimatedSeverity} · ${operationalImpact.businessImpact}`}
        >
          <p className="iw-intelligence__summary">{operationalImpact.businessImpact}</p>
          <p className="iw-section__copy iw-section__copy--muted">
            Affects {operationalImpact.affectedCapabilities.join(", ") || "unknown capabilities"} for{" "}
            {operationalImpact.affectedTenants.join(", ") || "unknown tenants"} · estimated severity{" "}
            {operationalImpact.estimatedSeverity}
          </p>
        </IntelGroup>
      ) : null}

      {recommendations.length > 0 ? (
        <IntelGroup
          title="Recommendations"
          summary={shortAction(recommendations[0]?.action ?? `${recommendations.length} action(s)`)}
        >
          <ul className="drawer-list">
            {recommendations.map((rec) => (
              <li key={`${rec.actionId ?? rec.action}`} className="drawer-list__item">
                <strong>{shortAction(rec.action)}</strong>
                <span>{rec.reason}</span>
                <span className="drawer-list__meta">
                  Expected outcome: {rec.expectedOutcome} · {rec.confidenceLabel} confidence (
                  {confidencePercent(rec.confidence)})
                  {rec.automationLevel ? ` · ${rec.automationLevel}` : ""}
                  {rec.estimatedDurationSeconds
                    ? ` · ~${Math.max(1, Math.round(rec.estimatedDurationSeconds / 60))} min`
                    : ""}
                </span>
                {rec.supportingEvidence.length > 0 ? (
                  <details>
                    <summary className="iw-intelligence__diag-summary">▶ Supporting evidence</summary>
                    <ul className="drawer-list iw-intelligence__nested">
                      {rec.supportingEvidence.map((line) => (
                        <li key={line} className="drawer-list__item">
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
                {rec.actionId && onExecuteAction ? (
                  <button
                    type="button"
                    className="btn btn-primary iw-intelligence__action"
                    disabled={actionsRunning}
                    onClick={() => {
                      if (rec.requiresConfirmation) {
                        const ok = window.confirm(
                          `Execute ${shortAction(rec.action)}? This action requires confirmation.`,
                        );
                        if (!ok) return;
                      }
                      onExecuteAction(rec.actionId!);
                    }}
                  >
                    {actionsRunning
                      ? "Running…"
                      : `Execute${rec.automationLevel === "safe" ? " · Safe" : ""}`}
                  </button>
                ) : rec.actionId ? (
                  <span className="drawer-list__meta">Executable action: {rec.actionId}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </IntelGroup>
      ) : null}

      {timeline.length > 0 ? (
        <IntelGroup title="Timeline" summary={`${timeline.length} events`}>
          <ul className="drawer-list">
            {timeline.map((event, index) => {
              const refs = [
                event.verificationRef ? `verification ${event.verificationRef}` : null,
                event.policyRef ? `policy ${event.policyRef}` : null,
                event.deploymentId ? `deployment ${event.deploymentId}` : null,
                event.incidentId ? `incident ${event.incidentId}` : null,
              ].filter(Boolean);
              return (
                <li key={`${event.component}-${event.timestamp}-${index}`} className="drawer-list__item">
                  <strong>
                    {EVENT_TYPE_LABELS[event.eventType]} · {event.component}
                    {event.status ? ` · ${event.status}` : ""}
                  </strong>
                  <span>{event.label}</span>
                  {event.detail ? <span>{event.detail}</span> : null}
                  <span className="drawer-list__meta">{formatRelativeTime(event.timestamp)}</span>
                  {refs.length > 0 ? (
                    <span className="drawer-list__meta">Refs: {refs.join(" · ")}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </IntelGroup>
      ) : null}

      {hasRelationships || correlatedIncidentIds.length > 0 ? (
        <IntelGroup
          title="Relationships"
          summary={
            relationships?.recurring
              ? "Recurring"
              : correlatedIncidentIds.length
                ? `${correlatedIncidentIds.length} correlated`
                : "Related events"
          }
        >
          {hasRelationships ? (
            <ul className="drawer-list">
              {relationships?.parentIncidentId ? (
                <li className="drawer-list__item">
                  <strong>Parent</strong>
                  <span>{relationships.parentIncidentId}</span>
                </li>
              ) : null}
              {relationships?.childIncidentIds.map((id) => (
                <li key={id} className="drawer-list__item">
                  <strong>Child</strong>
                  <span>{id}</span>
                </li>
              ))}
              {relationships?.duplicateIncidentIds.map((id) => (
                <li key={id} className="drawer-list__item">
                  <strong>Duplicate</strong>
                  <span>{id}</span>
                </li>
              ))}
              {relationships?.recurring ? (
                <li className="drawer-list__item">
                  <strong>Recurring</strong>
                  <span>Failed on {relationships.recurrenceCount} distinct days recently</span>
                </li>
              ) : null}
              {relationships?.relatedDeploymentIds.map((id) => (
                <li key={id} className="drawer-list__item">
                  <strong>Deployment</strong>
                  <span>{id}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {correlatedIncidentIds.length > 0 ? (
            <p className="iw-section__copy iw-section__copy--muted">
              Correlated with {correlatedIncidentIds.length} other event(s):{" "}
              {correlatedIncidentIds.join(", ")}
            </p>
          ) : null}
        </IntelGroup>
      ) : null}

      {narrative ? (
        <IntelGroup title="AI Narrative" summary={narrative.summary}>
          <dl className="iw-intelligence__narrative-grid">
            <div>
              <dt>Summary</dt>
              <dd>{narrative.summary}</dd>
            </div>
            <div>
              <dt>Root cause</dt>
              <dd>{narrative.rootCause}</dd>
            </div>
            <div>
              <dt>Impact</dt>
              <dd>{narrative.impact}</dd>
            </div>
            <div>
              <dt>Confidence</dt>
              <dd>{narrative.confidence}</dd>
            </div>
          </dl>
          {narrative.evidence.length > 0 ? (
            <details>
              <summary className="iw-intelligence__diag-summary">▶ Evidence</summary>
              <ul className="drawer-list">
                {narrative.evidence.map((line) => (
                  <li key={line} className="drawer-list__item">
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {narrative.recommendations.length > 0 ? (
            <details>
              <summary className="iw-intelligence__diag-summary">▶ Recommendations</summary>
              <ul className="drawer-list">
                {narrative.recommendations.map((line) => (
                  <li key={line} className="drawer-list__item">
                    <span>{shortAction(line)}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <details className="iw-intelligence__narrative">
            <summary>▶ Full briefing text</summary>
            <pre className="iw-intelligence__narrative-text">{narrative.fullText}</pre>
          </details>
        </IntelGroup>
      ) : null}
    </section>
  );
}
